/**
 * Cierra un desplegable cuando se hace clic fuera de él, o con Escape.
 *
 * 🔴 Reemplaza al capturador `<div className="fixed inset-0" onClick={cerrar} />`
 * que usaban los 9 desplegables del producto (#334). Ese truco depende de que el
 * `fixed` cubra la PANTALLA, y dentro de un ancestro con `backdrop-filter` (o
 * `transform`, o `filter`) deja de hacerlo: el ancestro se vuelve su bloque
 * contenedor y el div cubre solo ese bloque, así que un clic afuera no cierra
 * nada. Es lo que obligó a dejar `.cl-topbar` sin desenfoque.
 *
 * Escuchar el clic en el documento no depende de ningún bloque contenedor.
 *
 * ⚠️ `refs` tiene que incluir el DISPARADOR además del panel: si no, el clic que
 * abre el menú cerraría primero y el `onClick` del botón lo volvería a abrir.
 *
 * ⚠️ A diferencia del capturador, el clic afuera NO se traga: cierra el menú y
 * además llega a lo que se clicó. Es lo que hace cualquier menú del sistema
 * operativo, y evita el «doble clic» para usar lo de al lado.
 *
 * Se escucha `pointerdown` en fase de captura: un `stopPropagation` de un hijo
 * no puede impedir el cierre, y se decide antes de que el clic haga nada.
 */
import { useEffect, useRef, type RefObject } from 'react'

export function useCierreAlClicAfuera(
  abierto: boolean,
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  cerrar: () => void,
): void {
  // La función cambia en cada render; guardarla en un ref evita volver a
  // suscribir los listeners cada vez.
  const cerrarRef = useRef(cerrar)
  cerrarRef.current = cerrar
  const refsRef = useRef(refs)
  refsRef.current = refs

  useEffect(() => {
    if (!abierto) return

    const alPresionar = (e: PointerEvent) => {
      const destino = e.target as Node | null
      if (!destino) return
      const adentro = refsRef.current.some(r => r.current?.contains(destino))
      if (!adentro) cerrarRef.current()
    }
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrarRef.current()
    }

    document.addEventListener('pointerdown', alPresionar, true)
    document.addEventListener('keydown', alTeclear)
    return () => {
      document.removeEventListener('pointerdown', alPresionar, true)
      document.removeEventListener('keydown', alTeclear)
    }
  }, [abierto])
}
